import { useEffect, useState } from 'react';
import { callFn } from '../lib/api';
import type { Item } from '../lib/types';
import { useApp } from '../app/AppContext';
import { ItemTable } from '../components/ItemTable';
import { useLightbox } from '../components/Lightbox';

export function Shop() {
  const { categories, locations, settings, toast } = useApp();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('');
  const [cond, setCond] = useState('');
  const [loc, setLoc] = useState('');
  const [q, setQ] = useState('');
  const [view, setView] = useState<'card' | 'table'>(() => (localStorage.getItem('scb.shopView') === 'table' ? 'table' : 'card'));
  useEffect(() => { localStorage.setItem('scb.shopView', view); }, [view]);
  const [reserving, setReserving] = useState<Item | null>(null);
  const lb = useLightbox();
  const [monthlyUsed, setMonthlyUsed] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {};
      if (cat) payload.category_id = cat;
      if (cond) payload.condition = cond;
      if (loc) payload.location_id = loc;
      if (q.trim()) payload.q = q.trim();
      setItems(await callFn<Item[]>('list_items', payload));
    } catch (e) { toast(e instanceof Error ? e.message : 'Failed to load items', 'err'); }
    finally { setLoading(false); }
  }
  async function loadCount() {
    try {
      const rows = await callFn<{ status: string; reserved_at: string }[]>('list_my_reservations');
      const now = new Date();
      const n = rows.filter((r) => (r.status === 'reserved' || r.status === 'ready' || r.status === 'picked_up')
        && new Date(r.reserved_at).getMonth() === now.getMonth()
        && new Date(r.reserved_at).getFullYear() === now.getFullYear()).length;
      setMonthlyUsed(n);
    } catch { /* non-fatal */ }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [cat, cond, loc]);
  useEffect(() => { void loadCount(); /* eslint-disable-next-line */ }, []);

  const limit = settings?.monthly_reservation_limit ?? 3;
  const atLimit = monthlyUsed != null && monthlyUsed >= limit;
  const activeLocs = locations.filter((l) => l.is_active);

  return (
    <>
      <h1 className="page-title">Available donated items</h1>
      <p className="page-sub">Reserve items for your fosters. Each item shows where to pick it up.</p>

      <div className="toolbar">
        <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void load(); }} style={{ minWidth: 180 }} />
        <select value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={loc} onChange={(e) => setLoc(e.target.value)}>
          <option value="">All locations</option>
          {activeLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select value={cond} onChange={(e) => setCond(e.target.value)}>
          <option value="">New &amp; used</option>
          <option value="new">New</option>
          <option value="used">Used</option>
        </select>
        <button className="btn secondary small" onClick={() => void load()}>Search</button>
        <div className="view-toggle" role="group" aria-label="View">
          <button className={`view-btn${view === 'card' ? ' active' : ''}`} title="Card view" aria-pressed={view === 'card'} onClick={() => setView('card')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
          </button>
          <button className={`view-btn${view === 'table' ? ' active' : ''}`} title="Table view" aria-pressed={view === 'table'} onClick={() => setView('table')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><rect x="1" y="2" width="14" height="2.4" rx="1"/><rect x="1" y="6.8" width="14" height="2.4" rx="1"/><rect x="1" y="11.6" width="14" height="2.4" rx="1"/></svg>
          </button>
        </div>
        <div className="spacer" />
        <span className="limit-pill" title="Reservations counted this calendar month">
          {monthlyUsed ?? '—'} / {limit} this month
        </span>
      </div>

      {atLimit && <div className="notice info">You&rsquo;ve reached your monthly limit of {limit}. You can reserve more next month, or cancel one of your current reservations.</div>}

      {loading ? <div className="loading">Loading items…</div>
        : items.length === 0 ? <div className="empty">No items match — check back after the next donation drop. 🐈</div>
        : view === 'table' ? <ItemTable items={items} atLimit={atLimit} onReserve={(it) => setReserving(it)} />
        : (
          <div className="grid">
            {items.map((it) => (
              <div key={it.id} className="card item-card">
                {it.photo_ref
                  ? (
                    <button className="card-photo-btn" onClick={() => lb.open(it.photo_ref!, it.description, it.description)}
                      title="Click to see the full photo" aria-label={`View full photo of ${it.description}`}>
                      <img className="card-photo" src={it.photo_ref} alt={it.description} />
                    </button>
                  )
                  : <div className="card-photo empty">🐾</div>}
                <div className="desc">{it.description}</div>
                <div className="meta">
                  {it.category_name && <span className="badge cat">{it.category_name}</span>}
                  <span className={`badge ${it.condition}`}>{it.condition}</span>
                </div>
                <div className="meta">
                  <span className="qty">{it.quantity_available}</span> available
                  <span className="badge gray">📍 {it.location_name || 'location TBD'}</span>
                </div>
                {it.created_at && <div className="card-added">Added {new Date(it.created_at).toLocaleDateString()}</div>}
                {it.notes && <div className="item-notes">{it.notes}</div>}
                <button className="btn small" disabled={atLimit} onClick={() => setReserving(it)}>Reserve</button>
              </div>
            ))}
          </div>
        )}

      {lb.node}

      {reserving && (
        <ReserveModal
          item={reserving}
          onClose={() => setReserving(null)}
          onDone={() => { setReserving(null); void load(); void loadCount(); }}
        />
      )}
    </>
  );

  function ReserveModal({ item, onClose, onDone }: { item: Item; onClose: () => void; onDone: () => void }) {
    const [qty, setQty] = useState(1);
    const [busy, setBusy] = useState(false);
    // Default to the item's admin-set pickup location; '' = "to be confirmed".
    // The foster may change it — their choice applies only to THIS reservation.
    const [pickup, setPickup] = useState<string>(item.location_id ?? '');

    async function go() {
      setBusy(true);
      try {
        await callFn('reserve_item', { item_id: item.id, quantity: qty, location_id: pickup || null });
        toast('Reserved! Pick it up at the location you selected.', 'ok');
        onDone();
      } catch (e) { toast(e instanceof Error ? e.message : 'Could not reserve', 'err'); setBusy(false); }
    }

    return (
      <div className="modal-bg" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Reserve item</h3>
          <p style={{ marginTop: 0 }}><b>{item.description}</b></p>
          <div className="stack">
            <label className="field">Quantity
              <input type="number" min={1} max={item.quantity_available} value={qty}
                onChange={(e) => setQty(Math.max(1, Math.min(item.quantity_available, Number(e.target.value))))} />
            </label>
            <label className="field">Pickup location
              <select value={pickup} onChange={(e) => setPickup(e.target.value)}>
                <option value="">Pick up at a location to be confirmed.</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}{l.short_code ? ` (${l.short_code})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="muted" style={{ fontSize: 13, marginTop: -4 }}>
              Choose where you’d like to pick this up — this applies to your reservation only.
            </div>
            <div className="row">
              <button className="btn secondary" onClick={onClose}>Cancel</button>
              <button className="btn" onClick={go} disabled={busy}>{busy ? 'Reserving…' : 'Confirm reservation'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
